import * as THREE from 'three'
import { Heightfield } from '../terrain/heightfield'
import { ObstacleField } from '../physics/obstacles'
import type { Obstacle } from '../physics/obstacles'
import { lerp } from '../core/math'
import { withSunHaze } from './atmosphere'
import { createRockMaterial } from './rockMaterial'
import { createTreeMaterial, createTreeDepthMaterial, onTreeAtlasReady } from './treeMaterial'
import { buildCardTree } from './treeGeometry'
import { TREE_ARCHETYPES, ARCHETYPE_ORDER, UNIT_HEIGHT } from './treeArchetypes'
import type { ArchetypeKind } from './treeArchetypes'

/**
 * Trees and rocks as instanced meshes.
 *
 * The forest is bucketed along the fall line rather than built as one mesh per
 * tree type. One instanced mesh spanning the whole mountain has a bounding
 * sphere that also spans the whole mountain, so it is never outside the frustum
 * and never culled — every tree on the course was being submitted every frame,
 * and again for the shadow pass, including the several hundred behind the
 * camera. Bucketing costs a handful of draw calls and lets the other ninety
 * percent of the forest sit out most frames.
 *
 * That budget is what pays for the trees being worth drawing at all. Each
 * bucket carries a near and a far build and shows whichever suits its distance,
 * so the detailed conifer is only ever paid for within about a hundred metres —
 * which on a corridor this narrow is the only place it can be seen.
 */
export class ObstacleMesh {
  readonly group = new THREE.Group()
  private readonly buckets: Bucket[] = []
  /** Shared across every bucket; disposed once, not per mesh. */
  private readonly geometries: THREE.BufferGeometry[] = []
  private readonly materials: THREE.Material[] = []
  private readonly rockMesh: THREE.InstancedMesh | null = null

  constructor(field: ObstacleField, hf: Heightfield) {
    const trees = field.items.filter((o) => o.kind === 'tree')
    const rocks = field.items.filter((o) => o.kind === 'rock')

    const treeMat = createTreeMaterial()
    this.materials.push(treeMat)
    const treeDepth = createTreeDepthMaterial()
    this.materials.push(treeDepth)
    // Trees stay hidden until the atlas pixels are decoded — an unloaded map
    // samples black at alpha zero and the whole forest renders wrong for the
    // first beat. Rocks are unaffected, so only the trees wait.
    const treeGroup = new THREE.Group()
    treeGroup.visible = false
    this.group.add(treeGroup)
    onTreeAtlasReady(() => { treeGroup.visible = true })

    // Six geometries for the whole mountain: three archetypes, two builds each.
    const geo = new Map<string, THREE.BufferGeometry>()
    for (const kind of ARCHETYPE_ORDER) {
      for (const lod of ['near', 'far'] as const) {
        const g = buildCardTree(TREE_ARCHETYPES[kind][lod])
        geo.set(`${kind}:${lod}`, g)
        this.geometries.push(g)
      }
    }

    // --- Trees, bucketed along the fall line -------------------------------
    const byBucket = new Map<number, Obstacle[]>()
    for (const t of trees) {
      const b = Math.floor(t.z / BUCKET_METRES)
      const list = byBucket.get(b)
      if (list) list.push(t)
      else byBucket.set(b, [t])
    }

    const m = new THREE.Matrix4()
    const q = new THREE.Quaternion()
    const p = new THREE.Vector3()
    const s = new THREE.Vector3()
    const c = new THREE.Color()

    for (const items of byBucket.values()) {
      // The box comes from the trees themselves. The instanced bounding
      // *sphere* was the obvious thing to use and was far too generous: on a
      // corridor two hundred metres wide its radius is over a hundred metres,
      // so "distance minus radius" called buckets near when they were two
      // hundred metres off, and three of them ran the detailed build at once.
      const box = new THREE.Box3()
      for (const t of items) {
        box.expandByPoint(new THREE.Vector3(t.x, hf.sampleHeight(t.x, t.z), t.z))
      }
      box.expandByScalar(6)
      const bucket: Bucket = { box, near: [], far: [] }

      for (const kind of ARCHETYPE_ORDER) {
        const list = items.filter((t) => archetypeOf(t) === kind)
        if (list.length === 0) continue

        const flagged = TREE_ARCHETYPES[kind].near.windFlag > 0.4
        for (const lod of ['near', 'far'] as const) {
          const mesh = new THREE.InstancedMesh(geo.get(`${kind}:${lod}`)!, treeMat, list.length)
          list.forEach((t, i) => {
            const scale = t.height / UNIT_HEIGHT
            p.set(t.x, hf.sampleHeight(t.x, t.z) - 0.4, t.z)
            // Trees grow vertically, not slope-normal — a leaning forest reads
            // as a bug even though avalanche-swept trees really do lean.
            //
            // Flagged trees are the exception to the free spin. A wind-stripped
            // conifer carries its crown to leeward, and that only reads as
            // weather if every one of them on the mountain agrees which way the
            // wind blows. Spun at random they just look randomly damaged.
            q.setFromAxisAngle(UP, flagged
              ? WIND_BEARING + (spin(t.variant, 17) - 0.5) * 0.8
              : t.variant * Math.PI * 2)
            const girth = scale * lerp(0.80, 1.16, spin(t.variant, 3))
            s.set(girth, scale * lerp(0.86, 1.22, spin(t.variant, 7)), girth)
            m.compose(p, q, s)
            mesh.setMatrixAt(i, m)

            // Per-instance tint, so three geometries do not read as three
            // trees repeated. Multiplies the vertex colours, so it shifts the
            // needles and the snow together — a colder tree is colder all over,
            // which is how a stand under one sky actually looks.
            const v = spin(t.variant, 11)
            const warmth = lerp(0.90, 1.10, v)
            c.setRGB(warmth * lerp(0.94, 1.05, spin(t.variant, 13)), warmth, warmth * lerp(1.06, 0.95, v))
            mesh.setColorAt(i, c)
          })
          mesh.instanceMatrix.needsUpdate = true
          if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true
          mesh.castShadow = true
          mesh.receiveShadow = true
          // Cutout shadows: without the alpha-tested depth material every card
          // would cast its full quad and the floor would be dark rectangles.
          mesh.customDepthMaterial = treeDepth
          // The bounding sphere is computed from the instances, so it wraps
          // this bucket only — which is the entire point of bucketing.
          mesh.computeBoundingSphere()
          mesh.frustumCulled = true
          mesh.visible = lod === 'far'
          treeGroup.add(mesh)
          bucket[lod].push(mesh)
        }
      }

      this.buckets.push(bucket)
    }

    // --- Rocks -------------------------------------------------------------
    // Left as a single mesh. There are barely a hundred of them at forty
    // triangles each, so bucketing would buy a rounding error and cost real
    // draw calls. Snow settles on top of a boulder, so the cap is baked into
    // the vertex colours by height and every rock reads as sitting *in* the
    // snowpack rather than resting on it.
    const rockGeo = buildBoulder()
    this.geometries.push(rockGeo)
    if (rocks.length > 0) {
      const rockMat = withSunHaze(createRockMaterial(), 'fl-rock')
      this.materials.push(rockMat)
      const e = new THREE.Euler()
      const mesh = new THREE.InstancedMesh(rockGeo, rockMat, rocks.length)
      rocks.forEach((r, i) => {
        p.set(r.x, hf.sampleHeight(r.x, r.z) - r.height * 0.3, r.z)
        e.set(r.variant * 3.1, r.variant * 5.7, r.variant * 2.3)
        q.setFromEuler(e)
        s.set(r.radius * 1.25, r.height * 0.85, r.radius * 1.1)
        m.compose(p, q, s)
        mesh.setMatrixAt(i, m)
      })
      mesh.instanceMatrix.needsUpdate = true
      mesh.frustumCulled = false
      mesh.castShadow = true
      mesh.receiveShadow = true
      this.group.add(mesh)
      this.rockMesh = mesh
    }
  }

  /**
   * Pick a build per bucket from where the camera is.
   *
   * Distance to the nearest point of the bucket, not to its centre: a bucket
   * the rider is standing inside has a centre forty-five metres away, and
   * swapping the trees around them to the far build would be visible in the
   * worst possible place.
   */
  update(cameraPos: THREE.Vector3): void {
    for (const b of this.buckets) {
      const near = b.box.distanceToPoint(cameraPos) < NEAR_LOD_METRES
      for (const mesh of b.near) mesh.visible = near
      for (const mesh of b.far) mesh.visible = !near
    }
  }

  /** Meshes actually submitted, which is what the HUD should be reporting. */
  get drawCalls(): number {
    let n = this.rockMesh ? 1 : 0
    for (const b of this.buckets) {
      for (const mesh of b.near) if (mesh.visible) n++
      for (const mesh of b.far) if (mesh.visible) n++
    }
    return n
  }

  dispose(): void {
    for (const b of this.buckets) {
      for (const mesh of [...b.near, ...b.far]) mesh.dispose()
    }
    this.rockMesh?.dispose()
    for (const g of this.geometries) g.dispose()
    for (const mat of this.materials) mat.dispose()
  }
}

interface Bucket {
  /** Where the trees in this bucket actually are, not where the mesh says. */
  box: THREE.Box3
  near: THREE.InstancedMesh[]
  far: THREE.InstancedMesh[]
}

const UP = new THREE.Vector3(0, 1, 0)
/** Bucket depth along the fall line, metres. */
const BUCKET_METRES = 90
/**
 * Inside this, a bucket gets the detailed build.
 *
 * Deliberately tight. The detailed conifer is five hundred triangles against
 * the far build's hundred and thirty, so this distance is the entire cost
 * control for the forest, and on a corridor between twenty-six and eighty
 * metres wide there is very little worth resolving past forty metres — and with the forest half again denser, the near band is the whole cost lever.
 */
const NEAR_LOD_METRES = 38
/**
 * Which way the prevailing wind blows, radians about the vertical.
 *
 * The trees are the only thing on the mountain that record it, so it lives
 * here. Fall line runs down +z, so this puts the wind across the corridor
 * rather than down it — cross-loaded slopes are what build the wind slab the
 * snow model already has an opinion about.
 */
const WIND_BEARING = Math.PI * 0.5

/**
 * More decorrelated randomness out of the one number a tree carries.
 *
 * Obstacles are scattered by the sim and hold a single `variant`, which the old
 * build used for rotation, girth *and* height at once — so a tall tree was
 * always a fat tree that always faced the same way. Rendering is downstream of
 * the sim and cannot ask for more numbers without changing placement, and
 * changing placement would invalidate every recorded ghost and par on the
 * mountain. So the one number is stirred instead.
 */
function spin(variant: number, salt: number): number {
  const x = Math.sin(variant * 127.1 + salt * 31.7) * 43758.5453
  return x - Math.floor(x)
}

function archetypeOf(t: Obstacle): ArchetypeKind {
  if (t.variant < 0.44) return 'ghost'
  if (t.variant < 0.80) return 'spruce'
  return 'spar'
}

/** Unit boulder with snow baked onto its upward faces. */
function buildBoulder(): THREE.BufferGeometry {
  // Already non-indexed in this three build; calling toNonIndexed anyway logs
  // a warning every load, which is noise in a harness that treats warnings as
  // findings.
  const ico = new THREE.IcosahedronGeometry(1, 1)
  const g = ico.index ? ico.toNonIndexed() : ico
  const pos = g.attributes.position
  const colors = new Float32Array(pos.count * 3)

  // Roughen it so it is not an obvious sphere.
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i)
    const y = pos.getY(i)
    const z = pos.getZ(i)
    const jitter = 0.86 + 0.28 * Math.abs(Math.sin(x * 9.1 + y * 4.7 + z * 6.3))
    pos.setXYZ(i, x * jitter, y * jitter * 0.82, z * jitter)
  }
  g.computeVertexNormals()

  const nor = g.attributes.normal
  for (let i = 0; i < pos.count; i++) {
    // Faces looking up hold snow; the flanks stay rock.
    const upness = Math.max(0, nor.getY(i))
    const snow = Math.pow(upness, 1.6)
    const tone = 0.3 + 0.1 * Math.abs(Math.sin(pos.getX(i) * 5.3 + pos.getZ(i) * 3.1))
    colors[i * 3] = lerp(tone, 0.88, snow)
    colors[i * 3 + 1] = lerp(tone, 0.91, snow)
    colors[i * 3 + 2] = lerp(tone * 1.05, 0.96, snow)
  }
  g.setAttribute('color', new THREE.BufferAttribute(colors, 3))
  g.computeBoundingSphere()
  return g
}
